import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLists } from '../../contexts/ListContext';
import { 
  Box, 
  Button, 
  Dialog, 
  DialogActions, 
  DialogContent, 
  DialogTitle, 
  TextField, 
  List, 
  ListItemButton,
  ListItemText, 
  IconButton, 
  Typography, 
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ListIcon from '@mui/icons-material/List';

const ListManager: React.FC = () => {
  const { lists, createList, deleteList, updateList } = useLists();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [editingList, setEditingList] = useState<{id: string, name: string} | null>(null);
  const [hoveredList, setHoveredList] = useState<string | null>(null);

  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    
    try {
      await createList(newListName);
      setNewListName('');
      setOpen(false);
    } catch (error) {
      console.error('Failed to create list:', error);
    }
  };

  const handleUpdateList = async () => {
    if (!editingList || !editingList.name.trim()) return;
    
    try {
      await updateList(editingList.id, { name: editingList.name });
      setEditingList(null);
    } catch (error) {
      console.error('Failed to update list:', error);
    }
  };

  const handleDeleteList = async (listId: string) => {
    if (window.confirm('Are you sure you want to delete this list? This action cannot be undone.')) {
      try {
        await deleteList(listId);
      } catch (error) {
        console.error('Failed to delete list:', error);
      }
    }
  };

  return (
    <Box>
      <Box className="mylists" display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">My Lists</Typography>
        <Button 
          className="newlist"
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={() => setOpen(true)}
        >
          New List
        </Button>
      </Box>

      <List className="list-container">
        {lists.map((list) => (
          <Paper className="listbox"
            key={list.id}
            elevation={hoveredList === list.id ? 3 : 1}
            sx={{ 
              mb: 1,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-2px)'
              }
            }}
            onMouseEnter={() => setHoveredList(list.id)}
            onMouseLeave={() => setHoveredList(null)}
          >
            <ListItemButton
              className="listitem"
              onClick={() => navigate(`/list/${list.id}`)}
              sx={{ py: 1.5 }}
            >
              <ListIcon color="primary" sx={{ mr: 2 }} />
              <ListItemText 
                primary={
                  <Typography variant="subtitle1" fontWeight="medium">
                    {list.name}
                  </Typography>
                } 
                secondary={`${list.productIds.length} ${list.productIds.length === 1 ? 'item' : 'items'}`} 
              />
              <Box 
                onClick={(e) => e.stopPropagation()}
                sx={{ display: 'flex' }}
              >
                <IconButton 
                  className="list-edit"
                  edge="end" 
                  aria-label="edit" 
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setEditingList({ id: list.id, name: list.name });
                  }}
                  size="small"
                  sx={{ '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  className="list-delete"
                  edge="end" 
                  aria-label="delete"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleDeleteList(list.id);
                  }}
                  size="small"
                  sx={{ '&:hover': { bgcolor: 'error.light', color: 'error.contrastText' } }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </ListItemButton>
          </Paper>
        ))}
        {lists.length === 0 && (
          <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 2 }}>
            No lists yet. Create your first list to organize your favorites.
          </Typography>
        )}
      </List>

      {/* Create List Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Create New List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleCreateList()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateList} color="primary">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit List Dialog */}
      <Dialog open={!!editingList} onClose={() => setEditingList(null)}>
        <DialogTitle>Edit List</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="List Name"
            fullWidth
            value={editingList?.name || ''}
            onChange={(e) => editingList && setEditingList({...editingList, name: e.target.value})}
            onKeyPress={(e) => e.key === 'Enter' && handleUpdateList()}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditingList(null)}>Cancel</Button>
          <Button onClick={handleUpdateList} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListManager;
